import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";

const API_URL = process.env.REACT_APP_API_URL;

const getWeekDates = (offset = 0) => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + offset * 7);

  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, index) => {
    const date = new Date(startOfWeek.getTime() + index * 24 * 60 * 60 * 1000);
    return {
      label: `${day} - ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    };
  });
};

const WeeklyPayroll = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { activeCompany, isDemo } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0, stage: "" });
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDates = getWeekDates(weekOffset);
  const [weeklyHours, setWeeklyHours] = useState({});
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const employeesPerPage = 10;

  // Calculate pagination
  const totalPages = Math.ceil(employees.length / employeesPerPage);
  const startIndex = (currentPage - 1) * employeesPerPage;
  const endIndex = startIndex + employeesPerPage;
  const currentEmployees = employees.slice(startIndex, endIndex);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!auth || !auth.isAuthenticated) return;
    try {
      const token = auth.user?.access_token;
      const response = await fetch(`${API_URL}/subscription/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      }
    } catch (err) {
      console.error("Error fetching subscription status:", err);
    }
  }, [auth]);

  const fetchEmployees = useCallback(async () => {
    if (!auth || !auth.isAuthenticated || !activeCompany) { setLoading(false); return; }
    try {
      const token = auth.user?.access_token;
      const companyId = activeCompany.company_id;
      const response = await fetch(`${API_URL}/employees?company_id=${companyId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch employees: ${response.status}`);
      }

      const employeesData = await response.json();
      const sortedEmployees = employeesData.sort((a, b) => a.first_name.localeCompare(b.first_name));

      const weekDates = getWeekDates(weekOffset);
      const start_date = weekDates[0].date;
      const end_date = weekDates[6].date;

      const payrollResponse = await fetch(
        `${API_URL}/payroll_entries/date_range?start_date=${start_date}&end_date=${end_date}&company_id=${companyId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      let payrollData = {};
      if (payrollResponse.status === 404) {
        console.info("No payroll entries for this week.");
      } else if (payrollResponse.ok) {
        const payrollEntries = await payrollResponse.json();
        payrollData = payrollEntries.reduce((acc, entry) => {
          acc[entry.employee_id] = acc[entry.employee_id] || {};
          acc[entry.employee_id][entry.date] = {
            hours_worked: entry.hours_worked,
            payroll_id: entry.payroll_id,
          };
          return acc;
        }, {});
      }

      setEmployees(sortedEmployees);
      setWeeklyHours(payrollData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth, weekOffset, activeCompany]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchEmployees();
  }, [fetchSubscriptionStatus, fetchEmployees]);

  const handleHoursChange = (employeeId, dayIndex, value) => {
    // Allow empty string, digits, and a single decimal point while typing; clamp on blur
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    const hours = value;

    setWeeklyHours((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [weekDates[dayIndex].date]: {
          hours_worked: hours,
          payroll_id: prev[employeeId]?.[weekDates[dayIndex].date]?.payroll_id || null,
          isUpdated: true
        }
      },
    }));
  };

  // Advance to the next day input (used by Enter key)
  const advanceToNext = (employeeId, dayIndex) => {
    const allInputs = Array.from(document.querySelectorAll("input[data-employee]"));
    const employeeInputs = allInputs.filter((el) => el.getAttribute("data-employee") === employeeId);
    if (dayIndex < 6) {
      const next = employeeInputs[dayIndex + 1];
      if (next) { next.focus(); next.select(); }
    } else {
      const lastOfEmployee = employeeInputs[employeeInputs.length - 1];
      const globalIdx = allInputs.indexOf(lastOfEmployee);
      const next = allInputs[globalIdx + 1];
      if (next) { next.focus(); next.select(); }
    }
  };

  const handleHoursKeyDown = (e, employeeId, dayIndex) => {
    const { key } = e;

    if (key === "Enter" || key === "ArrowRight") {
      e.preventDefault();
      advanceToNext(employeeId, dayIndex);
      return;
    }

    if (key === "ArrowLeft") {
      e.preventDefault();
      const allInputs = Array.from(document.querySelectorAll("input[data-employee]"));
      const employeeInputs = allInputs.filter((el) => el.getAttribute("data-employee") === employeeId);
      if (dayIndex > 0) {
        const prev = employeeInputs[dayIndex - 1];
        if (prev) { prev.focus(); prev.select(); }
      } else {
        // Sunday: go to previous employee's Saturday
        const firstOfEmployee = employeeInputs[0];
        const globalIdx = allInputs.indexOf(firstOfEmployee);
        const prev = allInputs[globalIdx - 1];
        if (prev) { prev.focus(); prev.select(); }
      }
      return;
    }

    if (key === "ArrowDown") {
      e.preventDefault();
      const allInputs = Array.from(document.querySelectorAll("input[data-employee]"));
      // Find the input on the same day column for the next employee
      const current = allInputs.find(
        (el) => el.getAttribute("data-employee") === employeeId && parseInt(el.getAttribute("data-day"), 10) === dayIndex
      );
      const globalIdx = allInputs.indexOf(current);
      // Each employee has 7 inputs; step forward by 7 to land on the same day next row
      const next = allInputs[globalIdx + 7];
      if (next) { next.focus(); next.select(); }
      return;
    }

    if (key === "ArrowUp") {
      e.preventDefault();
      const allInputs = Array.from(document.querySelectorAll("input[data-employee]"));
      const current = allInputs.find(
        (el) => el.getAttribute("data-employee") === employeeId && parseInt(el.getAttribute("data-day"), 10) === dayIndex
      );
      const globalIdx = allInputs.indexOf(current);
      const prev = allInputs[globalIdx - 7];
      if (prev) { prev.focus(); prev.select(); }
    }
  };

  const handleHoursBlur = (employeeId, dayIndex) => {
    // Clamp value on blur
    setWeeklyHours((prev) => {
      const raw = prev[employeeId]?.[weekDates[dayIndex].date]?.hours_worked;
      if (raw === "" || raw == null) return prev;
      let hours = parseFloat(raw);
      if (isNaN(hours) || hours < 0) hours = 0;
      if (hours > 24) hours = 24;
      return {
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          [weekDates[dayIndex].date]: {
            ...prev[employeeId][weekDates[dayIndex].date],
            hours_worked: hours,
          },
        },
      };
    });
  };

  // Helper function to introduce a delay
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Helper function that performs a fetch with retry logic for DELETE requests
  const fetchWithRetry = async (url, options, maxAttempts = 3, baseDelay = 200) => {
    let attempts = 0;
    while (attempts < maxAttempts) {
      try {
        const res = await fetch(url, options);
        if (res.ok) {
          return res;
        } else if (res.status === 503) {
          attempts++;
          console.warn(`Delete attempt ${attempts} failed with status 503 for ${url}. Retrying...`);
          await delay(baseDelay * attempts);
        } else {
          console.error(`Delete failed with status ${res.status} for ${url}`);
          return res;
        }
      } catch (err) {
        attempts++;
        console.error(`Delete attempt ${attempts} encountered an error for ${url}:`, err);
        await delay(baseDelay * attempts);
      }
    }
    throw new Error(`Failed to delete after ${maxAttempts} attempts for ${url}`);
  };

  // Processes an array of delete task functions sequentially with retry logic.
  const processSequentialDeletes = async (deleteTasks) => {
    for (const task of deleteTasks) {
      try {
        await task();
      } catch (error) {
        console.error("Delete operation failed after retries:", error);
      }
      await delay(100);
    }
  };

  const hasActiveSubscription = () => {
    const status = subscriptionData?.subscriptionStatus?.toLowerCase();
    const isAdmin = subscriptionData?.is_admin === true;
    return isAdmin || status === "active" || status === "trialing" || status === "admin_bypass";
  };

  const DEMO_PAYROLL_LIMIT = 3;
  const demoPayrollCount = isDemo ? parseInt(localStorage.getItem("demo_payroll_count") || "0", 10) : 0;
  const canSubmit = hasActiveSubscription() || (isDemo && demoPayrollCount < DEMO_PAYROLL_LIMIT);

  const showSubscriptionPrompt = () => {
    Swal.fire({
      icon: 'info',
      title: t("subscription_required"),
      html: `
        <p>${t("sub_required_payroll_msg")}</p>
        <p class="mt-2">${t("sub_read_only_msg")}</p>
      `,
      showCancelButton: true,
      confirmButtonText: t("view_plans"),
      cancelButtonText: t("maybe_later"),
      confirmButtonColor: '#3b82f6'
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = '/ManageSubscription';
      }
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      showSubscriptionPrompt();
      return;
    }

    setIsSubmitting(true);
    const token = auth.user?.access_token;
    if (!token) return;

    const newEntries = [];
    const updatePromises = [];
    const deleteTasks = [];

    // Process ALL employees (not just current page) in batches
    employees.forEach((employee) => {
      const hoursPerDay = weeklyHours[employee.employee_id] || {};
      Object.keys(hoursPerDay).forEach((day) => {
        const payrollDate = weekDates.find((d) => d.date === day)?.date;
        if (!payrollDate) {
          console.error("Invalid date entry:", weekDates);
          return;
        }

        const payrollId = hoursPerDay[day].payroll_id;
        const hoursWorked = hoursPerDay[day].hours_worked;
        const computedTotal = employee.is_salary
          ? Number(employee.wage)
          : Number((hoursWorked * employee.wage).toFixed(2));

        if (payrollId && (hoursWorked === 0 || hoursWorked === "")) {
          deleteTasks.push(() =>
            fetchWithRetry(`${API_URL}/payroll_entries/${payrollId}`, {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            })
          );
        } else if (payrollId) {
          updatePromises.push(
            fetch(`${API_URL}/payroll_entries/${payrollId}`, {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                hours_worked: hoursWorked,
                total_pay: computedTotal,
                is_salary: employee.is_salary,
              }),
            })
          );
        } else if (hoursWorked > 0) {
          newEntries.push({
            employee_id: employee.employee_id,
            date: payrollDate,
            hours_worked: hoursWorked,
            total_pay: computedTotal,
            is_salary: employee.is_salary,
            company_id: activeCompany?.company_id || null,
          });
        }
      });
    });

    try {
      const totalOperations = deleteTasks.length + updatePromises.length + newEntries.length;
      let completedOperations = 0;

      // Process DELETE tasks sequentially with retries.
      if (deleteTasks.length > 0) {
        setSubmissionProgress({ current: completedOperations, total: totalOperations, stage: t("deleting_entries") });
        await processSequentialDeletes(deleteTasks);
        completedOperations += deleteTasks.length;
      }

      // Process update requests in smaller batches to prevent DynamoDB throttling
      if (updatePromises.length > 0) {
        setSubmissionProgress({ current: completedOperations, total: totalOperations, stage: t("updating_entries") });
        const batchSize = 10; // Smaller batches for updates
        for (let i = 0; i < updatePromises.length; i += batchSize) {
          const batch = updatePromises.slice(i, i + batchSize);
          await Promise.all(batch);
          completedOperations += batch.length;
          setSubmissionProgress({ current: completedOperations, total: totalOperations, stage: t("updating_entries") });
        }
      }

      // Process new entries in chunks of 10 (smaller batches for better reliability)
      if (newEntries.length > 0) {
        setSubmissionProgress({ current: completedOperations, total: totalOperations, stage: t("creating_entries") });
        const chunkSize = 10; // Reduced from 25 to 10 for better reliability
        for (let i = 0; i < newEntries.length; i += chunkSize) {
          const chunk = newEntries.slice(i, i + chunkSize);
          const response = await fetch(`${API_URL}/payroll_entries/bulk`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(chunk),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              `Failed to submit payroll chunk: ${errorData.detail || response.status}`
            );
          }

          completedOperations += chunk.length;
          setSubmissionProgress({ current: completedOperations, total: totalOperations, stage: t("creating_entries") });
        }
      }

      // Update weeklyHours state.
      setWeeklyHours((prev) => {
        const updatedHours = {};
        Object.keys(prev).forEach((employeeId) => {
          updatedHours[employeeId] = {};
          Object.keys(prev[employeeId]).forEach((date) => {
            if (prev[employeeId][date].hours_worked > 0) {
              updatedHours[employeeId][date] = {
                hours_worked: prev[employeeId][date].hours_worked,
                payroll_id: prev[employeeId][date].payroll_id,
                isUpdated: false,
              };
            }
          });
        });
        return updatedHours;
      });

      if (isDemo) {
        const newCount = parseInt(localStorage.getItem("demo_payroll_count") || "0", 10) + 1;
        localStorage.setItem("demo_payroll_count", String(newCount));
      }
      Swal.fire({
        icon: "success",
        title: t("payroll_submitted"),
        text: t("payroll_submitted_msg")
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("submission_error"),
        text: error.message || "Failed to submit payroll."
      });
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress({ current: 0, total: 0, stage: "" });
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut} disableNavigation={isSubmitting}>
      <div className="flex flex-col items-center justify-center flex-1 p-3 sm:p-4">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full max-w-7xl">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2 sm:mb-4">{t("weekly_payroll_title")}</h1>
        <p className="text-gray-600 mb-4 sm:mb-6 text-sm">{t("weekly_payroll_subtitle")}</p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-gray-400 text-sm">Loading employees…</span>
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block max-h-96 overflow-y-auto border border-gray-300 rounded-lg overflow-x-visible">
              <table className="w-full bg-white border border-gray-300 text-sm table-fixed" style={{ tableLayout: 'fixed' }}>
                <thead className="sticky top-0 bg-gray-200">
                  <tr>
                    <th className="px-2 py-2 border text-center">{t("col_employee")}</th>
                    {weekDates.map((entry, index) => (
                      <th key={index} className="px-2 py-2 border text-xs" style={{ width: '80px' }}>{entry.label}</th>
                    ))}
                    <th className="px-2 py-2 border" style={{ width: '90px' }}>{t("col_total_hours")}</th>
                    <th className="px-2 py-2 border" style={{ width: '80px' }}>{t("col_wage_header")}</th>
                    <th className="px-2 py-2 border text-center">{t("col_total")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentEmployees.map((employee) => (
                    <tr key={employee.employee_id} className="hover:bg-gray-100">
                      <td className="px-2 py-2 border text-center">
                        {employee.first_name} {employee.last_name}
                      </td>
                      {weekDates.map((_entry, index) => (
                        <td key={index} className="px-2 py-2 border">
                          <input
                            type="text"
                            inputMode="numeric"
                            data-employee={employee.employee_id}
                            data-day={index}
                            className={`w-14 p-1 border rounded text-center ${weeklyHours[employee.employee_id]?.[weekDates[index].date]?.isUpdated ? "bg-gray-300" : ""}`}
                            value={weeklyHours[employee.employee_id]?.[weekDates[index].date]?.hours_worked ?? ""}
                            onChange={(e) => handleHoursChange(employee.employee_id, index, e.target.value)}
                            onKeyDown={(e) => handleHoursKeyDown(e, employee.employee_id, index)}
                            onBlur={() => handleHoursBlur(employee.employee_id, index)}
                            onFocus={(e) => e.target.select()}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2 border font-semibold text-center">
                        {Object.values(weeklyHours[employee.employee_id] || {}).reduce((sum, entry) =>
                          sum + (entry?.hours_worked ? Number(entry.hours_worked) : 0), 0
                        ).toFixed(2)}
                      </td>
                      <td className="px-2 py-2 border">
                        {employee.is_salary ? `$${Number(employee.wage).toFixed(2)}` : `$${employee.wage}/hr`}
                      </td>
                      <td className="px-2 py-2 border font-bold text-center">
                        {employee.is_salary
                          ? `$${Number(employee.wage).toFixed(2)}`
                          : `$${(
                              Object.values(weeklyHours[employee.employee_id] || {}).reduce((sum, entry) =>
                                sum + (entry?.hours_worked ? Number(entry.hours_worked) : 0), 0
                              ) * employee.wage
                            ).toFixed(2)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="block lg:hidden space-y-3">
              {currentEmployees.map((employee) => {
                const totalHrs = Object.values(weeklyHours[employee.employee_id] || {}).reduce(
                  (sum, e) => sum + (e?.hours_worked ? Number(e.hours_worked) : 0), 0
                );
                const totalPay = employee.is_salary
                  ? Number(employee.wage).toFixed(2)
                  : (totalHrs * employee.wage).toFixed(2);
                return (
                  <div key={employee.employee_id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="font-semibold text-gray-800 mb-2 text-sm">{employee.first_name} {employee.last_name}</p>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekDates.map((_entry, index) => {
                        const abbr = ["Su","Mo","Tu","We","Th","Fr","Sa"][index];
                        return (
                          <div key={index} className="flex flex-col items-center">
                            <span className="text-xs text-gray-400 mb-0.5">{abbr}</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              data-employee={employee.employee_id}
                              data-day={index}
                              className={`w-full p-0.5 border rounded text-center text-xs ${
                                weeklyHours[employee.employee_id]?.[weekDates[index].date]?.isUpdated ? "bg-gray-300" : "bg-white"
                              }`}
                              value={weeklyHours[employee.employee_id]?.[weekDates[index].date]?.hours_worked ?? ""}
                              onChange={(e) => handleHoursChange(employee.employee_id, index, e.target.value)}
                              onKeyDown={(e) => handleHoursKeyDown(e, employee.employee_id, index)}
                              onBlur={() => handleHoursBlur(employee.employee_id, index)}
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-gray-600 pt-1 border-t border-gray-200">
                      <span>{totalHrs.toFixed(2)} hrs</span>
                      <span className="text-gray-500">{employee.is_salary ? `$${Number(employee.wage).toFixed(2)}` : `$${employee.wage}/hr`}</span>
                      <span className="font-semibold text-gray-800">${totalPay}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination Controls */}
        {employees.length > employeesPerPage && (
          <div className="flex justify-center items-center mt-4 space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1 || isSubmitting}
              className={`px-3 py-1 rounded ${
                currentPage === 1 || isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {t("prev_page")}
            </button>

            <span className="px-3 py-1 text-sm text-gray-600">
              {t("page_of", { current: currentPage, total: totalPages })}
            </span>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || isSubmitting}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages || isSubmitting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              {t("next_page")}
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        {isSubmitting && submissionProgress.total > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-blue-800">{submissionProgress.stage}</span>
              <span className="text-sm text-blue-600">
                {submissionProgress.current} / {submissionProgress.total}
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(submissionProgress.current / submissionProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6 gap-3">
          <div className="flex gap-2">
            <button
              disabled={isSubmitting}
              className={`flex-1 sm:flex-none bg-gray-300 px-4 py-2 rounded ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"}`}
              onClick={() => setWeekOffset(weekOffset - 1)}
            >
              {t("prev_week")}
            </button>
            <button
              disabled={isSubmitting}
              className={`flex-1 sm:flex-none bg-gray-300 px-4 py-2 rounded ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400"}`}
              onClick={() => setWeekOffset(weekOffset + 1)}
            >
              {t("next_week")}
            </button>
          </div>

          <button
            className={`py-2 px-4 rounded transition ${
              !canSubmit
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isSubmitting
                ? 'bg-green-400 text-white opacity-50 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            title={!canSubmit ? (isDemo ? `Demo limit reached (${DEMO_PAYROLL_LIMIT} submissions)` : 'Subscription required to submit payroll') : ''}
          >
            {isSubmitting ? t("processing") : canSubmit ? t("submit_payroll") : (isDemo ? `Demo limit reached (${demoPayrollCount}/${DEMO_PAYROLL_LIMIT})` : t("subscribe_to_submit"))}
          </button>
        </div>
        <Link to="/" style={isSubmitting ? { pointerEvents: "none", opacity: "0.5" } : {}} className="block mt-6 text-center">
          <button className="w-full bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600">
            {t("back_to_dashboard_btn")}
          </button>
        </Link>
        </div>
      </div>
      </Sidebar>
    </div>
  );
};

export default WeeklyPayroll;
